import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { waveQueries } from '@round/api';
import type { wave } from '@round/api';
import type { CampaignStatus } from '@/types/creator';
import { getInviteLifecycleStatus } from '@/utility/campaignDisplay';
import { getLeftmostNonEmptyCampaignStatus } from '@/utility/creatorCampaigns';
import { countOffers, getOfferSummary } from '@/utility/contracts';
import { handleTabKeyDown } from '@/utility/tabKeyboard';
import CampaignCard from './CampaignCard';

type StatusFilter = {
    id: CampaignStatus;
    label: string;
};

const STATUS_FILTERS: StatusFilter[] = [
    { id: 'invited', label: 'Invited' },
    { id: 'active', label: 'Accepted' },
    { id: 'closed', label: 'Closed' },
    { id: 'completed', label: 'Completed' },
    { id: 'declined', label: 'Declined' },
];
const STATUS_FILTER_IDS = STATUS_FILTERS.map(({ id }) => id);

const CampaignsPage = () => {
    const navigate = useNavigate();

    const invitesQuery = useQuery(waveQueries.invite.options.mine());
    // Only tells cards which invites hold several offers, so a failed read leaves each on its own offer.
    const contractsQuery = useQuery(waveQueries.contract.options.mine());
    // Invites don't carry the campaign's budget reading, so it's looked up by campaign; a failed read hides the meter.
    const campaignsQuery = useQuery(waveQueries.creatorCampaign.options.mine());
    const isLoading = invitesQuery.isLoading;
    const isError = invitesQuery.isError;

    const invites = useMemo(() => invitesQuery.data ?? [], [invitesQuery.data]);

    const invitesWithStatus = useMemo(
        () =>
            invites.map((invite) => ({
                invite,
                status: getInviteLifecycleStatus(invite),
            })),
        [invites]
    );

    // Null means the creator has not chosen a tab yet. This lets async data select
    // the leftmost non-empty bucket without an effect overwriting a later click.
    const [selectedStatusOverride, setSelectedStatusOverride] = useState<CampaignStatus | null>(null);

    const statusCounts = Object.fromEntries(
        STATUS_FILTERS.map(({ id }) => [id, invitesWithStatus.filter((item) => item.status === id).length])
    ) as Record<CampaignStatus, number>;
    const defaultStatus = getLeftmostNonEmptyCampaignStatus(statusCounts, STATUS_FILTER_IDS);
    const selectedStatus = selectedStatusOverride ?? defaultStatus;
    const filteredInvites = invitesWithStatus.filter((item) => item.status === selectedStatus);
    const selectedLabel = STATUS_FILTERS.find(({ id }) => id === selectedStatus)?.label.toLowerCase();

    const contractsByInviteId = new Map<string, wave.CreatorContract[]>();
    for (const contract of contractsQuery.data ?? []) {
        contractsByInviteId.set(contract.invite, [...(contractsByInviteId.get(contract.invite) ?? []), contract]);
    }

    const budgetUsedPercentageByCampaignId = new Map(
        (campaignsQuery.data ?? []).map((campaign) => [campaign.id, campaign.budget_used_percentage])
    );

    const openCampaign = (inviteId: string): void => {
        navigate(`/campaigns/${inviteId}`);
    };

    return (
        <div className="page campaigns-page">
            <header className="page-heading">
                <div>
                    <h1>Campaigns</h1>
                    <p>View and manage your campaign activity.</p>
                </div>
            </header>

            {isLoading ? (
                <div className="campaign-filter-empty">
                    <p>Loading your campaigns…</p>
                </div>
            ) : isError ? (
                <div className="campaign-filter-empty">
                    <h2>Couldn&apos;t load your campaigns</h2>
                    <p>Something went wrong. Try again shortly.</p>
                </div>
            ) : (
                <>
                    <div className="campaign-status-tabs" role="tablist" aria-label="Campaign status">
                        {STATUS_FILTERS.map(({ id, label }) => (
                            <button
                                type="button"
                                role="tab"
                                id={`campaign-status-${id}-tab`}
                                aria-selected={selectedStatus === id}
                                aria-controls="campaign-status-panel"
                                tabIndex={selectedStatus === id ? 0 : -1}
                                className={selectedStatus === id ? 'is-active' : ''}
                                data-status={id}
                                key={id}
                                onClick={() => setSelectedStatusOverride(id)}
                                onKeyDown={(event) =>
                                    handleTabKeyDown(
                                        event,
                                        STATUS_FILTER_IDS,
                                        selectedStatus,
                                        setSelectedStatusOverride
                                    )
                                }
                            >
                                <span>{label}</span>
                                <strong>{statusCounts[id]}</strong>
                            </button>
                        ))}
                    </div>
                    <section
                        id="campaign-status-panel"
                        className="campaign-filter-results"
                        role="tabpanel"
                        aria-labelledby={`campaign-status-${selectedStatus}-tab`}
                        aria-label={`${selectedLabel} campaigns`}
                        key={selectedStatus}
                    >
                        {filteredInvites.length > 0 ? (
                            <div className="invite-list">
                                {filteredInvites.map(({ invite, status }) => (
                                    <CampaignCard
                                        key={invite.id}
                                        invite={invite}
                                        offerCount={countOffers(contractsByInviteId.get(invite.id) ?? [])}
                                        offerSummary={getOfferSummary(contractsByInviteId.get(invite.id) ?? [])}
                                        status={status}
                                        budgetUsedPercentage={
                                            budgetUsedPercentageByCampaignId.get(invite.campaign_id) ?? null
                                        }
                                        onOpenCampaign={openCampaign}
                                    />
                                ))}
                            </div>
                        ) : (
                            <div className="campaign-filter-empty">
                                <h2>No {selectedLabel} campaigns</h2>
                                <p>Campaigns will appear here when their status changes.</p>
                            </div>
                        )}
                    </section>
                </>
            )}
        </div>
    );
};

export default CampaignsPage;
